import { getAuthToken } from './auth/tokenStorage';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../config';

export interface Employment {
  id: string;
  taskId: number;
  companyName?: string;
  jobTitle?: string;
  workAddress?: string;
  workPhone?: string;
  workEmail?: string;
  supervisorName?: string;
  supervisorPhone?: string;
  supervisorEmail?: string;
  hrContactName?: string;
  hrContactPhone?: string;
  hrContactEmail?: string;
  employeeId?: string;
  startDate?: Date;
  salary?: number;
  employmentType?: string;
  benefitsInfo?: string;
  additionalNotes?: string;
  retirementDate?: string;
  hasPension?: boolean;
  pensionDetails?: string;
  isLocked: boolean;
}

export interface CreateEmploymentData {
  taskId: number;
  companyName?: string;
  jobTitle?: string;
  workAddress?: string;
  workPhone?: string;
  workEmail?: string;
  supervisorName?: string;
  supervisorPhone?: string;
  supervisorEmail?: string;
  hrContactName?: string;
  hrContactPhone?: string;
  hrContactEmail?: string;
  employeeId?: string;
  startDate?: Date;
  salary?: number;
  employmentType?: string;
  benefitsInfo?: string;
  additionalNotes?: string;
  retirementDate?: string;
  hasPension?: boolean;
  pensionDetails?: string;
}

export interface UpdateEmploymentData extends CreateEmploymentData {
  isLocked?: boolean;
}

class EmploymentService {
  private async getAuthToken(): Promise<string | null> {
    return await getAuthToken();
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
    });

    return response;
  }

  async getEmployment(taskId: number): Promise<Employment[]> {
    const response = await this.makeRequest(`/api/employment-info?taskId=${taskId}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch employment info: ${response.status}`);
    }

    return await response.json();
  }

  async getAllEmployment(targetUserId?: string): Promise<Employment[]> {
    const params = targetUserId ? `?targetUserId=${targetUserId}` : '';
    const response = await this.makeRequest(`/api/employment-info${params}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch all employment info: ${response.status}`);
    }

    return await response.json();
  }

  async getEmploymentById(id: string): Promise<Employment> {
    const response = await this.makeRequest(`/api/employment-info/${id}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch employment info: ${response.status}`);
    }

    return await response.json();
  }

  async createEmployment(data: CreateEmploymentData): Promise<{ id: string }> {
    const response = await this.makeRequest('/api/employment-info', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to create employment info: ${response.status}`);
    }
    
    return await response.json();
  }

  async updateEmployment(id: string, data: UpdateEmploymentData): Promise<Employment> {
    const response = await this.makeRequest(`/api/employment-info/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to update employment info: ${response.status}`);
    }

    const result = await response.json();
    return result.data || result;
  }

  async deleteEmployment(id: string): Promise<void> {
    const response = await this.makeRequest(`/api/employment-info/${id}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      throw new Error(`Failed to delete employment info: ${response.status}`);
    }
  }

  async lockEmployment(id: string): Promise<Employment> {
    const response = await this.makeRequest(`/api/employment-info/${id}/lock`, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to lock employment: ${response.status}`);
    }

    return await response.json();
  }

  async unlockEmployment(id: string, selectedEmails: string[]): Promise<Employment> {
    const response = await this.makeRequest(`/api/employment-info/${id}/unlock`, {
      method: 'POST',
      body: JSON.stringify({ selectedEmails }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to unlock employment: ${response.status}`);
    }

    return await response.json();
  }

  async getEmploymentAccessGrants(id: string): Promise<any[]> {
    const response = await this.makeRequest(`/api/employment-info/${id}/grants`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to get employment access grants: ${response.status}`);
    }

    return await response.json();
  }
}

export const employmentService = new EmploymentService();